import { combineReducers } from 'redux';
import cloneDeep from 'lodash/cloneDeep';

import commonStoreReducer from '../../CommonDataStore/reducer';
import metaDataReducer from '../../MetaDataStore/reducer';

import {
    COMMON_DATA_LIST_EVENTS_ERROR,
    COMMON_DATA_LIST_EVENTS_LOADING_SUCCESS,
    COMMON_DATA_LIST_EVENTS_START_LISTENING_FOR_WS_UPDATES,
    COMMON_DATA_LIST_EVENTS_STOP_LISTENING_FOR_WS_UPDATES,
    COMMON_DATA_LIST_EVENTS_UPDATE_MARKET_ID,
} from './../actions/commonDataListEventsActionsTypes';
import mainSocket from '../../../../../shared/services/mainSocketClass';
import { WS_HANDLER_COMPONENT_NAME, WS_MSG_ACTION_INPLAY_START, WS_MSG_ACTION_INPLAY_UPDATE } from '../constants';
import {
    COMMON_REDUX_STORE_UPDATE_FROM_WEBSOCKET_EVENTS,
    COMMON_REDUX_STORE_CLEAR,
    COMMON_REDUX_STORE_UPDATE_FROM_BACKEND,
} from '../../CommonDataStore/actionTypes';
import { addNewInplayEventsFromWs } from '../utils/utils';
import { eventIsOver } from '../../../sport-event/utils';

const defaultState = { events: [] };

/**
 * Main Common Data List Events reducer
 * Note that there is a market_id property stored with an event. It is used on page load and is a
 * legacy construct. It should not be relied on with the market switcher (or in general). It should
 * be removed when we migrate the CommonDataStore to the Spectate store.
 * @param {*} state the current state
 * @param {*} action the action
 * @returns {*} new state
 */
function commonDataListEventsReducer(state = defaultState, action) {
    switch (action.type) {
        case COMMON_DATA_LIST_EVENTS_LOADING_SUCCESS: {
            // sportSlug only used for a isGenericSport() check
            const { events = [], sportSlug } = action;

            // Careful now. This *could* cause bugs if nested objects/arrays are shallow copied. Leaving this
            // comment in case someone arrives here - see
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax#sect1
            return { ...state, events: events.map((e) => ({ ...e, sportSlug })), errorOccurred: false };
        }
        case COMMON_DATA_LIST_EVENTS_START_LISTENING_FOR_WS_UPDATES: {
            const { selectionPointers } = action;
            if (!selectionPointers.length) {
                return state;
            }

            /**
             * @todo AM-2540 We could remove these handlers - do we need to for a shared common store?
             * "Is it safe?" https://www.youtube.com/watch?v=mqrpPaNm8yw
             */
            mainSocket.subscribeForUpdates(selectionPointers, WS_HANDLER_COMPONENT_NAME);

            return { ...state, selectionPointers };
        }
        case COMMON_DATA_LIST_EVENTS_ERROR: {
            return { ...state, errorOccurred: action.errorOccurred };
        }
        case COMMON_DATA_LIST_EVENTS_STOP_LISTENING_FOR_WS_UPDATES: {
            const { selectionPointers = [] } = state;
            if (!selectionPointers.length) {
                return state;
            }
            const { events = [] } = action;
            const payload = events.length
                ? selectionPointers.filter(({ event_id: selectionPointerEventId }) => {
                        return events.find(event => {
                            const eventId = typeof event === 'object'
                                ? event.event_id
                                : event;

                            return eventId === selectionPointerEventId;
                        });
                    })
                : selectionPointers;

            if (payload.length) {
                mainSocket.unsubscribeForUpdates(payload, WS_HANDLER_COMPONENT_NAME);
            }

            return state;
        }
        case COMMON_REDUX_STORE_CLEAR: {

            return defaultState;
        }
        case COMMON_DATA_LIST_EVENTS_UPDATE_MARKET_ID: {
            // market switcher update to common store.
            const { newSelectionPointers = null } = action;
            if (newSelectionPointers === null) {

                return state;
            }

            const newState = { ...state };
            newState.selectionPointers = [...newSelectionPointers];

            return newState;
        }
        case COMMON_REDUX_STORE_UPDATE_FROM_WEBSOCKET_EVENTS: {
            const { wsData } = action;
            const { action: wsAction } = wsData;

            if (wsAction === WS_MSG_ACTION_INPLAY_START) {
                const { newState, unsubscribeEventIds, newSelectionPointers } = addNewInplayEventsFromWs(state, wsData);

                const unsubscribePayload = [];
                const { selectionPointers: oldStateSelectionPointers } = state;
                oldStateSelectionPointers.forEach((selectionPointer) => {
                    if (unsubscribeEventIds.has(selectionPointer.event_id)) {
                        unsubscribePayload.push(selectionPointer);
                    }
                });
                // if these are ever empty we have an issue with the WS message
                if (unsubscribePayload.length) {
                    mainSocket.unsubscribeForUpdates(unsubscribePayload);
                }
                if (newSelectionPointers.length) {
                    mainSocket.subscribeForUpdates(newSelectionPointers);
                }

                return newState;
            }

             // within inplay_update message we have to handle the scenario
            // when event is finished, remove it from state and unsubscribe from ws
            if (wsAction === WS_MSG_ACTION_INPLAY_UPDATE) {
                const newState = { ...state };
                const wsEventsIdsToRemove = wsData.events.filter(wsEvent => eventIsOver(wsEvent.match_status)).map(wsEvent => wsEvent.id);

                if (wsEventsIdsToRemove.length) {
                    const unsubscribePayload = [];
                    const activeSelectionPointers = [];
                    newState.events = newState.events.filter(event => !wsEventsIdsToRemove.includes(event.event_id));
                    newState.selectionPointers.forEach(pointer => {
                        (wsEventsIdsToRemove.includes(pointer.event_id) ? unsubscribePayload : activeSelectionPointers).push(pointer);
                    });
                    newState.selectionPointers = activeSelectionPointers;
                    mainSocket.unsubscribeForUpdates(unsubscribePayload);
                }

                return newState;
            }

            return state;
        }
        case COMMON_REDUX_STORE_UPDATE_FROM_BACKEND: {
            const { selectionPointers = null } = action;
            if (selectionPointers === null) {

                return state;
            }

            /**
             * Following code is used when we switch markets on a competitions/tournament page. Will be used
             * with market_switcher AM-4823 soon. We replace markets and need to replace selection_pointers
             * so we can (un)subscribe accordingly for websocket updates. (Until now the commonDataStore
             * generally only contained the default_market).
             */
            const newState = cloneDeep(state);

            newState.selectionPointers = selectionPointers;

            return newState;
        }
        default:
            return state;
    }
}

export default combineReducers({
    commonDataListEvents: commonDataListEventsReducer,
    commonData: commonStoreReducer,
    metaData: metaDataReducer,
});
